import { useEffect, useState } from 'react';
import { Alert, Button, Card, Stack, Text, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { subscribeSync, type SyncStatus } from '../offline/sync.js';
import {
  currentSubscription,
  isIos,
  isStandalone,
  pushSupported,
  subscribeToPush,
} from '../offline/push.js';

/** Разрешение спрашивается после первого сданного осмотра, а не при первом запуске. */
type PushPrompt = 'hidden' | 'ask' | 'install' | 'done';

export function Done(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sync, setSync] = useState<SyncStatus>({
    running: false,
    pending: 0,
    lastError: null,
    lastSyncAt: null,
  });
  const [prompt, setPrompt] = useState<PushPrompt>('hidden');

  useEffect(() => subscribeSync(setSync), []);

  useEffect(() => {
    // Осмотр ещё в очереди — не отвлекаем: сначала пусть уйдёт.
    if (sync.pending > 0) return;
    if (!pushSupported()) {
      // На iOS вкладка Safari уведомлений не получит никогда: сначала установка.
      if (isIos() && !isStandalone()) setPrompt('install');
      return;
    }
    if (isIos() && !isStandalone()) {
      setPrompt('install');
      return;
    }
    void currentSubscription().then((sub) => {
      setPrompt(sub === null && Notification.permission === 'default' ? 'ask' : 'hidden');
    });
  }, [sync.pending]);

  return (
    <div className="pf-screen">
      <div className="pf-scroll">
        <Stack gap="lg" maw={520} mx="auto" w="100%" justify="center" style={{ flex: 1 }}>
          <Title order={1}>{t('done.title')}</Title>

          {sync.pending > 0 ? (
            <Alert color="orange" variant="light">
              {t('done.queued')}
            </Alert>
          ) : (
            <Card>
              {/* Приложение не выносит вердикт «эксплуатация запрещена»:
                  оно фиксирует факт и уведомляет ответственного (правило 16). */}
              <Text>{t('done.notified')}</Text>
            </Card>
          )}

          {prompt === 'ask' && (
            <Card>
              <Text mb="sm">{t('push.ask')}</Text>
              <Button
                fullWidth
                onClick={() => {
                  void subscribeToPush()
                    .then(() => setPrompt('done'))
                    .catch(() => setPrompt('hidden'));
                }}
              >
                {t('push.enable')}
              </Button>
            </Card>
          )}

          {prompt === 'install' && (
            <Card>
              <Text>{t('push.install')}</Text>
            </Card>
          )}

          {prompt === 'done' && (
            <Alert color="blue" variant="light">
              {t('push.done')}
            </Alert>
          )}
        </Stack>
      </div>

      <div className="pf-action-bar">
        <Button fullWidth onClick={() => void navigate('/units')}>
          {t('done.again')}
        </Button>
      </div>
    </div>
  );
}
